/**
 * Application state store (localStorage-backed).
 *
 * Faithful to the monolith's `state` object + loadState/saveState, wrapped in a
 * small observable store. On load it migrates the monolith's conflated
 * `activeCharges` array by splitting charge-slug strings (scoring) from
 * free-text `{id,text}` charge descriptions (the Case-tab list).
 */

import type { AppState, ChargeSlug, ChargeDescription } from '@/types';
import { CHARGE_SLUGS } from '@/types';

export const STORAGE_KEY = 'voirai_state';

export function defaultState(): AppState {
  return {
    side: 'defense',
    panelLens: 'defense',
    activeCharges: [],
    chargeList: [],
    caseName: '',
    court: 'Suffolk Superior',
    county: 'Suffolk',
    trialDate: '',
    judge: '',
    opp: '',
    defenseAtty: '',
    caseNotes: '',
    statute: '',
    savedCases: [],
    crimeElements: [],
    selectedCaseIdx: '',
    jurors: [],
    researchCharge: 'murder',
    vdCharge: 'all',
    vdFilter: 'all',
    lastJurorDeletion: null,
    nextId: 100,
    docket: '',
    courtSpecific: '',
    prossBBO: '',
    defBBO: '',
    indictmentFacts: '',
    motionSavedText: '',
    attyAffSavedText: '',
    prosAddr: '',
    prosEmail: '',
    prosPhone: '',
    defAddr: '',
    defEmail: '',
    defPhone: '',
    chargeDescription: '',
    caseProfile: { evidence: 'close', pretrialPublicity: 'low', localCommunityImpact: false },
    victims: [],
    defendant: null
  };
}

const isChargeSlug = (v: unknown): v is ChargeSlug =>
  typeof v === 'string' && (CHARGE_SLUGS as readonly string[]).includes(v);

/**
 * Reconcile a loaded state blob against the current shape, splitting any
 * legacy conflated `activeCharges` into slugs + free-text descriptions.
 */
export function migrateLoadedState(saved: Partial<AppState> & Record<string, unknown>): AppState {
  const base = { ...defaultState(), ...saved } as AppState;

  const rawCharges: unknown[] = Array.isArray(saved.activeCharges) ? saved.activeCharges : [];
  const slugs = rawCharges.filter(isChargeSlug);
  const legacyDescriptions = rawCharges.filter(
    (c): c is ChargeDescription =>
      typeof c === 'object' && c !== null && 'text' in (c as object)
  );

  base.activeCharges = slugs;
  base.chargeList = Array.isArray(saved.chargeList) ? saved.chargeList : legacyDescriptions;
  return base;
}

type Listener = (state: AppState) => void;

export class Store {
  private state: AppState;
  private readonly listeners = new Set<Listener>();

  constructor(initial: AppState = defaultState()) {
    this.state = initial;
  }

  /** The live state object. Mutate it directly, then call {@link save}. */
  getState(): AppState {
    return this.state;
  }

  /** Persist to localStorage and notify subscribers (mirrors saveState()). */
  save(): void {
    this.persist();
    this.emit();
  }

  /** Merge a partial patch, then persist + notify. */
  setState(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    this.save();
  }

  /** Load persisted state, migrating legacy shapes. No-op if none/unavailable. */
  load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      this.state = migrateLoadedState(JSON.parse(raw));
    } catch (e) {
      console.error('Failed to load state:', e);
    }
  }

  /** Reset to defaults and clear storage. */
  reset(): void {
    this.state = defaultState();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
    this.emit();
  }

  subscribe(cb: Listener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private emit(): void {
    for (const cb of this.listeners) cb(this.state);
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (e) {
      console.error('Failed to save state:', e);
    }
  }
}
